import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getMyPayments, getChurchProfile } from '../../services/api';
import { FiClock, FiCheckCircle, FiXCircle, FiImage, FiEye } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import ReceiptViewModal from '../../components/ReceiptViewModal';
import AuthImg from '../../components/AuthImg';
import useAppRefresh from '../../hooks/useAppRefresh';

const statusPill = { pending: 'pill-yellow', approved: 'pill-green', rejected: 'pill-red' };
const statusIcon = { pending: <FiClock />, approved: <FiCheckCircle />, rejected: <FiXCircle /> };

const MyPayments = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState('');
  const [viewImg, setViewImg] = useState(null);
  const [profile, setProfile] = useState({});
  const [viewCR, setViewCR] = useState(null);

  const loadPayments = () => getMyPayments().then(r => setPayments(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { loadPayments(); }, []);
  useEffect(() => { getChurchProfile().then(r => setProfile(r.data || {})).catch(() => {}); }, []);
  useAppRefresh(loadPayments);

  const filtered = filter ? payments.filter(p => p.status === filter) : payments;

  const viewReceipt = (p) => {
    setViewCR({
      cr_number: p.cr_number,
      date: p.cr_date || p.reviewed_at?.slice(0, 10) || p.submitted_at?.slice(0, 10),
      received_from: user?.name || '',
      member_card: p.member_card || '',
      spiritual_father_name: p.cr_spiritual_father_name || '',
      hagest_sbket: p.cr_hagest_sbket || profile.hagest_sbket || '',
      city: p.cr_city || profile.city || '',
      fund_name: p.fund_name || '',
      payment_method: p.payment_method || '',
      amounts: p.amounts || {},
      notes: p.notes || '',
      received_by_name: p.reviewed_by_name || '',
      verify_token: p.cr_verify_token || '',
    });
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.myPayments')}</h1><p>{payments.length}</p></div>
      </div>

      <div className="filters-row">
        {['', 'pending', 'approved', 'rejected'].map(s => (
          <button key={s} className={`btn ${filter === s ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => setFilter(s)}>
            {s === '' ? t('common.all') : t(`paymentStatus.${s}`)}
          </button>
        ))}
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr><th>#</th><th>{t('common.date')}</th><th>{t('finance.fund')}</th>{(profile.currencies && profile.currencies.length ? profile.currencies : ['$','K']).map(sym => <th key={sym}>Amount {sym}</th>)}<th>{t('finance.paymentMethod')}</th><th>{t('common.actions')}</th><th>{t('common.status')}</th><th>{t('finance.crNumber')}</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7 + (profile.currencies && profile.currencies.length ? profile.currencies.length : 2)}><div className="empty-state"><p>{t('finance.noRecords')}</p></div></td></tr>
            ) : filtered.map((p, i) => {
              // v1.6.11 — if the linked CR was deleted by the treasurer, treat
              // the row as reversed: strikethrough the amounts, dim the row,
              // swap the green Approved pill for a red 'Reversed' pill.
              const reversed = !!p.cr_deleted_at;
              const amtStyle = reversed ? { textDecoration: 'line-through', color: '#9ca3af' } : undefined;
              const rowStyle = reversed ? { opacity: 0.75 } : undefined;
              return (
              <tr key={p.id} style={rowStyle}>
                <td data-label="#">{i + 1}</td>
                <td data-label={t('common.date')}>{p.submitted_at?.slice(0, 16)}</td>
                <td data-label={t('finance.fund')}>{p.fund_name || '—'}</td>
                {(profile.currencies && profile.currencies.length ? profile.currencies : ['$','K']).map(sym => {
                  const v = parseFloat((p.amounts || {})[sym] || 0);
                  return <td key={sym} data-label={`Amount ${sym}`} style={amtStyle}>{v > 0 ? `${sym}${v.toFixed(2)}` : '—'}</td>;
                })}
                <td data-label={t('finance.paymentMethod')} style={{ textTransform: 'capitalize' }}>{p.payment_method}</td>
                <td data-label={t('common.actions')}>
                  {p.screenshot
                    ? <button className="btn btn-sm btn-secondary" onClick={() => setViewImg(p.screenshot)}><FiImage /> {t('approve.viewScreenshot')}</button>
                    : '—'}
                </td>
                <td data-label={t('common.status')}>
                  {reversed ? (
                    <>
                      <span className="pill pill-red" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <FiXCircle /> {t('paymentStatus.reversed', { defaultValue: 'Reversed' })}
                      </span>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>
                        {t('myPayments.reversedHint', { defaultValue: 'Cash Receipt reversed by the treasurer.' })}
                      </div>
                    </>
                  ) : (
                    <>
                      <span className={`pill ${statusPill[p.status]}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {statusIcon[p.status]} {t(`paymentStatus.${p.status}`)}
                      </span>
                      {p.status === 'rejected' && p.rejection_reason && (
                        <div style={{ fontSize: 11, color: '#dc2626', marginTop: 3 }}>{t('myProfile.reasonLabel')}: {p.rejection_reason}</div>
                      )}
                    </>
                  )}
                </td>
                <td data-label={t('finance.crNumber')}>
                  {p.cr_number ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span className="pill pill-gray" style={{ fontFamily: 'monospace', fontSize: 11, textDecoration: reversed ? 'line-through' : undefined }}>{p.cr_number}</span>
                      {!reversed && (
                        <button className="btn btn-sm btn-secondary" onClick={() => viewReceipt(p)} title={t('pendingApprovals.viewBtn')}>
                          <FiEye /> {t('pendingApprovals.viewBtn')}
                        </button>
                      )}
                    </div>
                  ) : '—'}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {/* Receipt view modal */}
      {viewCR && <ReceiptViewModal cr={viewCR} profile={profile} onClose={() => setViewCR(null)} />}

      {/* Screenshot preview modal */}
      {viewImg && (
        <div className="modal-overlay" onClick={() => setViewImg(null)} style={{ alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <button
            onClick={() => setViewImg(null)}
            aria-label="Close"
            style={{ position: 'fixed', top: 14, right: 14, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 40, height: 40, fontSize: 22, cursor: 'pointer', zIndex: 10 }}
          >✕</button>
          <div style={{ maxWidth: 600, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }} onClick={e => e.stopPropagation()}>
            <AuthImg filename={viewImg} alt="Screenshot" style={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }} fallback={<div style={{ color: '#fff', padding: 24 }}>Loading…</div>} />
            <button className="btn btn-secondary" onClick={() => setViewImg(null)}>{t('common.close')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPayments;
